import { createFileRoute, Link } from "@tanstack/react-router";
import { getErrorMessage } from "@/lib/errorMessage";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, ShieldCheck, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  createRefundRequest,
  getMyRefundRequests,
  getRefundEligibleOrders,
  getRefundReasons,
  REFUND_WINDOW_DAYS,
  type RefundEligibleOrder,
} from "@/lib/support/refunds.functions";

export const Route = createFileRoute("/_authenticated/account_/refunds")({
  validateSearch: (search: Record<string, unknown>) => ({
    order: typeof search.order === "string" ? search.order : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Refund requests · TruWe" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: ({ error, reset }: { error: unknown; reset: () => void }) => (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="font-display text-2xl font-semibold">Couldn't load refunds</h1>
      <p className="mt-2 text-sm text-muted-foreground">{getErrorMessage(error)}</p>
      <Button className="mt-4" onClick={reset}>Retry</Button>
    </div>
  ),
  notFoundComponent: () => <div className="p-12 text-center">Not found</div>,
  component: RefundsPage,
});

const MAX_PHOTOS = 4;
const MAX_BYTES = 8 * 1024 * 1024;

function rupees(paise: number) {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function RefundsPage() {
  const queryClient = useQueryClient();
  const eligibleFn = useServerFn(getRefundEligibleOrders);
  const reasonsFn = useServerFn(getRefundReasons);
  const listFn = useServerFn(getMyRefundRequests);
  const createFn = useServerFn(createRefundRequest);

  const eligible = useQuery({ queryKey: ["refund-eligible"], queryFn: () => eligibleFn({}) });
  const reasons = useQuery({ queryKey: ["refund-reasons"], queryFn: () => reasonsFn({}) });
  const requests = useQuery({ queryKey: ["refund-requests"], queryFn: () => listFn({}) });

  const search = Route.useSearch();
  const [orderId, setOrderId] = useState<string>("");
  const [selected, setSelected] = useState<Record<string, number>>({});
  const [reasonCode, setReasonCode] = useState<string>("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  const orders: RefundEligibleOrder[] = eligible.data?.orders ?? [];
  useEffect(() => {
    if (!orderId && search.order && orders.some((o) => o.orderId === search.order)) {
      setOrderId(search.order);
    }
  }, [search.order, orders, orderId]);
  const order = orders.find((o) => o.orderId === orderId) ?? null;


  function reset() {
    setOrderId("");
    setSelected({});
    setReasonCode("");
    setDescription("");
    setFiles([]);
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= MAX_PHOTOS) break;
      if (!f.type.startsWith("image/")) {
        toast.error("Only image files can be attached.");
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is larger than 8 MB.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  }

  const submit = useMutation({
    mutationFn: async () => {
      if (!order) throw new Error("Select an order.");
      const items = Object.entries(selected)
        .filter(([, qty]) => qty > 0)
        .map(([orderItemId, quantity]) => ({ orderItemId, quantity }));
      if (!items.length) throw new Error("Select at least one item.");
      if (!reasonCode) throw new Error("Pick a reason.");
      if (description.trim().length < 10) throw new Error("Tell us a bit more (10+ characters).");
      if (!files.length) throw new Error("Add at least one photo.");

      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) throw new Error("Please sign in again.");

      setUploading(true);
      const folder = `${uid}/${crypto.randomUUID()}`;
      const evidence: Array<{ path: string; mimeType: string; sizeBytes: number }> = [];
      try {
        for (const [i, file] of files.entries()) {
          const ext = (file.name.split(".").pop() || "jpg").toLowerCase().slice(0, 5);
          const path = `${folder}/${i + 1}.${ext}`;
          const { error } = await supabase.storage
            .from("support-evidence")
            .upload(path, file, { upsert: false, contentType: file.type });
          if (error) throw new Error("Photo upload failed. Please try again.");
          evidence.push({ path, mimeType: file.type, sizeBytes: file.size });
        }
      } finally {
        setUploading(false);
      }

      return createFn({
        data: {
          orderId: order.orderId,
          reasonCode,
          description: description.trim(),
          items,
          evidence,
        },
      });
    },
    onSuccess: (res) => {
      toast.success(`Request ${res.ticketNumber} submitted. We'll review it manually.`);
      reset();
      queryClient.invalidateQueries({ queryKey: ["refund-requests"] });
      queryClient.invalidateQueries({ queryKey: ["account", "requests"] });
      queryClient.invalidateQueries({ queryKey: ["refund-eligible"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const busy = submit.isPending || uploading;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link
        to="/account"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to account
      </Link>

      <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight">Refund requests</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Raise a request within {REFUND_WINDOW_DAYS} days of delivery. Every request is reviewed
        by a human — submitting one does not automatically refund your payment.
      </p>

      {/* ── New request ─────────────────────────────────────────── */}
      <section className="mt-8 rounded-2xl border border-border bg-card p-5 md:p-6">
        <h2 className="font-display text-lg font-semibold">Start a new request</h2>

        {eligible.isLoading ? (
          <p className="mt-4 text-sm text-muted-foreground">Checking your recent orders…</p>
        ) : !orders.length ? (
          <p className="mt-4 text-sm text-muted-foreground">
            No orders are currently eligible. Requests can be raised for paid orders delivered in
            the last {REFUND_WINDOW_DAYS} days.
          </p>
        ) : (
          <div className="mt-5 space-y-5">
            <div>
              <Label className="text-sm">Order</Label>
              <select
                className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                value={orderId}
                onChange={(e) => {
                  setOrderId(e.target.value);
                  setSelected({});
                }}
              >
                <option value="">Choose an eligible order</option>
                {orders.map((o) => (
                  <option key={o.orderId} value={o.orderId}>
                    {`${o.orderName} · ${rupees(o.totalPaise)} · ${o.daysLeft} day${o.daysLeft === 1 ? "" : "s"} left`}
                  </option>
                ))}
              </select>
            </div>

            {order && (
              <>
                <div>
                  <Label className="text-sm">Items to refund</Label>
                  <div className="mt-2 space-y-2">
                    {order.items.map((it) => {
                      const qty = selected[it.orderItemId] ?? 0;
                      return (
                        <div
                          key={it.orderItemId}
                          className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5"
                        >
                          <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                            <input
                              type="checkbox"
                              className="h-4 w-4 shrink-0 accent-[hsl(var(--brand))]"
                              checked={qty > 0}
                              onChange={(e) =>
                                setSelected((s) => ({
                                  ...s,
                                  [it.orderItemId]: e.target.checked ? 1 : 0,
                                }))
                              }
                            />
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{it.title}</span>
                              <span className="text-xs text-muted-foreground">
                                Ordered {it.quantity} · {rupees(it.unitPricePaise)} each
                              </span>
                            </span>
                          </label>
                          {qty > 0 && (
                            <select
                              className="rounded-lg border border-border bg-background px-2 py-1 text-sm"
                              value={qty}
                              onChange={(e) =>
                                setSelected((s) => ({
                                  ...s,
                                  [it.orderItemId]: Number(e.target.value),
                                }))
                              }
                            >
                              {Array.from({ length: it.quantity }, (_, i) => i + 1).map((n) => (
                                <option key={n} value={n}>
                                  Qty {n}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Reason</Label>
                  <select
                    className="mt-1.5 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value)}
                  >
                    <option value="">What went wrong?</option>
                    {(reasons.data?.reasons ?? []).map((r) => (
                      <option key={r.code} value={r.code}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  {reasonCode && (
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {reasons.data?.reasons.find((r) => r.code === reasonCode)?.description}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm" htmlFor="refund-description">
                    Describe the issue
                  </Label>
                  <Textarea
                    id="refund-description"
                    className="mt-1.5"
                    rows={4}
                    maxLength={1500}
                    placeholder="Tell us what happened so our team can resolve it quickly."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div>
                  <Label className="text-sm">Photos ({files.length}/{MAX_PHOTOS})</Label>
                  <p className="text-xs text-muted-foreground">
                    1–4 clear photos are required. Include the product and the outer pack.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <div
                        key={`${f.name}-${i}`}
                        className="relative h-20 w-20 overflow-hidden rounded-xl border border-border"
                      >
                        <img
                          src={URL.createObjectURL(f)}
                          alt={`Evidence ${i + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          aria-label="Remove photo"
                          onClick={() => setFiles(files.filter((_, idx) => idx !== i))}
                          className="absolute right-1 top-1 rounded-full bg-background/90 p-0.5"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {files.length < MAX_PHOTOS && (
                      <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-xs text-muted-foreground hover:border-foreground/40">
                        <ImagePlus className="h-4 w-4" />
                        Add
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            addFiles(e.target.files);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2 rounded-xl bg-muted/50 p-3 text-xs text-muted-foreground">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Submitting records your request only. Our team reviews it manually and no
                    money moves until it is approved.
                  </span>
                </div>

                <Button
                  className="w-full"
                  disabled={busy}
                  onClick={() => submit.mutate()}
                >
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {uploading ? "Uploading photos…" : "Submit refund request"}
                </Button>
              </>
            )}
          </div>
        )}
      </section>

      {/* ── History lives on the tracking page ──────────────────── */}
      <section className="mt-8 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Track your requests</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {requests.data?.requests.length
            ? `You have ${requests.data.requests.length} refund request${requests.data.requests.length === 1 ? "" : "s"}. See status and full history in one place.`
            : "All your return, refund and cancellation requests — with status history — live here."}
        </p>
        <Link
          to="/account/requests"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-sm font-medium hover:border-brand hover:text-brand"
        >
          View your requests
        </Link>
      </section>

    </div>
  );
}
